import { ApolloQueryResult, OperationVariables } from '@apollo/client';
import { DocumentNode } from 'graphql';
import {
	selector,
	atomFamily,
	selectorFamily,
	RecoilValueReadOnly,
	SerializableParam
} from 'recoil';
import { defined } from '../defined';

import { apollo as client } from '../../graphql-client';

type DefaultVariables = {
	[key: string]: never;
};

const DEFAULT_VARIABLES: DefaultVariables = {};

export const createQuerySelector = <
	TQuery,
	TVariables extends SerializableParam = DefaultVariables
>(
	key: string,
	query: DocumentNode,
	variablesSelector?: RecoilValueReadOnly<TVariables | undefined>,
	requireVariables = false
): RecoilValueReadOnly<ApolloQueryResult<TQuery> | undefined> => {
	const defaultState = selectorFamily<
		ApolloQueryResult<TQuery> | undefined,
		TVariables | DefaultVariables
	>({
		key: `${key}__default`,
		get: variables => () => {
			const typedVars = variables as OperationVariables;

			return client.query<TQuery, OperationVariables>({
				query,
				variables: typedVars
			});
		}
	});

	const queryAtom = atomFamily<
		ApolloQueryResult<TQuery> | undefined,
		TVariables | DefaultVariables
	>({
		key: `${key}__atom`,
		default: defaultState,
		effects_UNSTABLE: variables => [
			({ setSelf }) => {
				const typedVars = variables as OperationVariables;
				const res = client.watchQuery<TQuery, OperationVariables>({
					query,
					variables: typedVars
				});

				const sub = res.subscribe({
					next: setSelf,
					error: console.error,
					complete: console.log,
					start: console.log
				});

				return sub.unsubscribe;
			}
		]
	});

	return selector<ApolloQueryResult<TQuery> | undefined>({
		key,
		get({ get }) {
			let variables: TVariables | DefaultVariables;

			if (!defined(variablesSelector)) {
				if (requireVariables) return;

				variables = DEFAULT_VARIABLES;
			} else {
				const selectorVariables = get(variablesSelector);

				if (!defined(selectorVariables)) {
					if (requireVariables) return;

					variables = DEFAULT_VARIABLES;
				} else {
					variables = selectorVariables;
				}
			}

			return get(queryAtom(variables));
		}
	});
};
